import React, { ReactElement } from 'react';

import cls from 'classnames';

import { lang as l10nLang, formatMessage } from '@ott/l10n';
import formatMoney from '@ott/utility-format-money';

import styles from './BaseMoney.scss';

const spaceRegexp = /\u00A0/g;

export const currenciesMap = {
  'uk-ua': 'UAH',
  'ru-ua': 'UAH',
  'kk-kz': 'KZT',
  'ru-kz': 'KZT',
  'en-gb': 'GBP',
  'en-us': 'USD',
  'en-ie': 'EUR',
  'ru-by': 'BYN',
  'en-ch': 'CHF',
  'fr-ch': 'CHF',
  'de-ch': 'CHF',
  'en-ae': 'AED',
  de: 'EUR',
  es: 'EUR',
  kz: 'KZT',
  tr: 'TRY',
  pl: 'PLN',
  br: 'EUR',
  az: 'EUR',
  ru: 'RUB',
  fr: 'EUR',
  'ru-us': 'USD',
  'ru-uz': 'UZS',
};

export type Currencies = typeof currenciesMap;
export const getCurrency = (lang: keyof typeof currenciesMap): Currencies[keyof Currencies] =>
  currenciesMap[lang];

export type NumberFormatOptions = {
  /** Минимальное количество цифр после точки */
  minimumFractionDigits?: number;
  /** Максимальное количество цифр после точки */
  maximumFractionDigits?: number;
  /** Форматирование, которое должно отображаться для числа */
  notation?: 'compact' | 'standard' | 'scientific' | 'engineering';
};

export type BaseMoneyProps = {
  /** Количество */
  amount: number;
  /** Валюта */
  currency?: Currencies[keyof Currencies];
  /** Язык */
  lang?: string;
  /** Объект с опциями форматирования числа */
  numberFormatOptions?: NumberFormatOptions;
  /** Жирный текст */
  isFat?: boolean;
  /** Отображение символа + */
  showPlusSign?: boolean;
  /** Отображение символа валюты */
  useSymbol?: boolean;
  /** Замена символа валюты */
  symbolReplacer?: string;
  /** Отображение только число */
  asNumber?: boolean;
  /** URL для кастомной иконки валюты */
  customCurrencyIconUrl?: string;
  /** Кастомный объект для тэга style */
  style?: Record<string, string>;
  /** Дополнительные стили */
  className?: string;
};

export const BaseMoney = (props: BaseMoneyProps): ReactElement => {
  const {
    lang = l10nLang,
    numberFormatOptions = {},
    amount = 0,
    isFat = false,
    showPlusSign = false,
    useSymbol = true,
    symbolReplacer = '',
    asNumber = false,
    style,
    customCurrencyIconUrl = '',
    currency,
    className,
  } = props;

  const currentCurrency = currency ?? getCurrency(lang as keyof typeof currenciesMap);
  const formattedAmount = formatMoney(Math.abs(amount), {
    currencyCode: currentCurrency,
    lang,
    numberFormatOptions,
    useSymbol,
    asNumber,
  });

  const getContentNode = () => {
    const symbolMatch = /[^\w\s]+$/gi.exec(formattedAmount);
    const contentNode: ReactElement[] = [];

    if (amount < 0) {
      contentNode.push(<span key="sign">&minus;</span>);
    }

    if (amount >= 0 && showPlusSign) {
      contentNode.push(<span key="sign">+</span>);
    }

    if (!symbolMatch) {
      contentNode.push(<span key="formattedAmount">{formattedAmount}</span>);

      return contentNode;
    }

    const symbol = symbolMatch[0];
    const before = formattedAmount.substr(0, symbolMatch.index);
    const after = formattedAmount.substr(symbolMatch.index + symbol.length);

    const symbolNode = customCurrencyIconUrl ? (
      <span key="customMoneySymbol" className={styles.customMoneySymbol}>
        <img
          key="customCurrencyIcon"
          className={styles.customCurrencyIcon}
          src={customCurrencyIconUrl}
          alt="customCurrencyIcon"
        />
      </span>
    ) : (
      <span key="symbol">
        {symbolReplacer
          ? formatMessage(
              {
                id: 'money.WLSymbolReplacer',
                defaultMessage: symbolReplacer,
              },
              { amount: parseInt(before.replace(spaceRegexp, ''), 10) }
            )
          : symbol}
      </span>
    );

    contentNode.push(
      <span key="before">{before}</span>,
      symbolNode,
      <span key="after">{after}</span>
    );

    return contentNode;
  };

  const dataAttributes = Object.keys(props).reduce((acc, key) => {
    if (key.startsWith('data-')) {
      // @ts-ignore
      const { [key]: dataAttribute } = props;
      // @ts-ignore
      acc[key] = dataAttribute;
    }

    return acc;
  }, {});

  return (
    <span
      className={cls(styles.baseMoney, className, {
        [styles.isFat]: isFat,
      })}
      style={style}
      /* eslint-disable-next-line react/jsx-props-no-spreading */
      {...dataAttributes}
    >
      {getContentNode()}
    </span>
  );
};
